import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, Link } from "react-router-dom";
import Select, { components } from "react-select";
import AsyncSelect from "react-select/async";
import { Button, Row, Col, Card, CardBody, Container, FormGroup, Label, Input } from "reactstrap";
import moment from "moment";

import Breadcrumb from "../../../layout/breadcrumb";
import withReducer from "../../../store/withReducer";
import orderReducer from "../store";
import BootstrapTable from "../../../share-components/table/BootstrapTable";
import DateRangePickerDefault from "../../../share-components/ui/DateRangePickerDefault";
import { getOrderSettings, setSettings } from "../store/orderSlice";
import { sellerAutoSuggestionApi } from "../../../services/manage-product/productService";
import { mapSearchAutoSuggestion } from "../../../utils/mapper";

import {
    getTrackings,
    downloadTrackingLabel,
    downloadTrackingLabelQRCode,
} from "../store/trackingSlice";

const OrderTrackings = () => {
    const trackingState = useSelector(({ order }) => order.tracking);
    const orderState = useSelector(({ order }) => order.order);
    const dispatch = useDispatch();
    const history = useHistory();
    const queryParams = new URLSearchParams(window.location.search);

    const isValidDate = (d) => d !== null && d !== undefined && moment(d).isValid();
    const _rawEnd = queryParams.has("end_date") ? new Date(queryParams.get("end_date")) : undefined;
    const _rawStart = queryParams.has("start_date") ? new Date(queryParams.get("start_date")) : undefined;
    const endDateObj = isValidDate(_rawEnd) ? _rawEnd : undefined;
    const startDateObj = isValidDate(_rawStart) ? _rawStart : undefined;

    const defaultEndDate = endDateObj ?? new Date();
    const defaultStartDate = startDateObj ?? moment().subtract(29, "days").toDate();

    const [dateRange, setDateRange] = useState([defaultStartDate, defaultEndDate]);
    const [startDate, endDate] = dateRange;

    const [loading, setLoading] = useState(true);
    const [orderNumberInput, setOrderNumberInput] = useState("");
    const [trackingNumberInput, setTrackingNumberInput] = useState("");
    const [trackingStatus, setTrackingStatus] = useState(null);
    const [trackingType, setTrackingType] = useState(null);
    const [sellerFilter, setSellerFilter] = useState(null);
    const [deliveryPartnerFilter, setDeliveryPartnerFilter] = useState(null);

    const [filters, setFilters] = useState({
        page: 1,
        pageSize: 10,
        start_date: isValidDate(startDate) ? moment(startDate).format("YYYY-MM-DD") : null,
        end_date: isValidDate(endDate) ? moment(endDate).format("YYYY-MM-DD") : null,
        orderNumber: "",
        trackingNumber: "",
        tracking_status: null,
        tracking_type: null,
        seller_id: null,
        delivery_partner_id: null,
    });

    const timerRef = useRef(null);

    useEffect(() => {
        dispatch(getOrderSettings()).then((res) => {
            if (res.payload) dispatch(setSettings(res.payload));
        });
    }, [dispatch]);

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    const sellerLoadOptions = (inputValue) =>
        new Promise((resolve) => {
            const data = { value: inputValue || "", limit: 200 };
            setTimeout(() => {
                resolve(mapSearchAutoSuggestion(sellerAutoSuggestionApi(data)));
            });
        });

    const trackingStatusOptions = useMemo(() => ([
        { id: 1, name: "Pending" },
        { id: 2, name: "In Transit" },
        { id: 3, name: "Delivered" },
    ]), []);

    const trackingTypeOptions = useMemo(() => ([
        { id: 1, name: "Order" },
        { id: 2, name: "Return" },
        { id: 3, name: "Decline Return" },
        { id: 4, name: "TB One Return" },
        { id: 5, name: "Alteration" },
        { id: 6, name: "Alteration Request" },
        { id: 7, name: "Alteration Processed" },
    ]), []);

    const dataMenu = [
        {
            type: 1,
            text: "Orders",
            link: "/order",
        },
        {
            type: 0,
            text: "Trackings",
        },
    ];

    useEffect(() => {
        setFilters((prev) => ({
            ...prev,
            start_date: isValidDate(startDate) ? moment(startDate).format("YYYY-MM-DD") : null,
            end_date: isValidDate(endDate) ? moment(endDate).format("YYYY-MM-DD") : null,
            page: 1,
        }));
    }, [startDate, endDate]);

    const apiFilters = useMemo(() => {
        const pageSizeForApi = Number(filters.pageSize) || 10;
        return { ...filters, pageSize: pageSizeForApi };
    }, [filters]);

    useEffect(() => {
        setLoading(true);
        dispatch(getTrackings(apiFilters))
            .finally(() => {
                setLoading(false);
            });
    }, [dispatch, apiFilters]);

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
            page: 1,
        }));
    };

    const handleTextInputChange = (key, value) => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
            setFilters((prev) => ({
                ...prev,
                [key]: value,
                page: 1,
            }));
        }, 500);
    };

    const clearFilters = () => {
        setDateRange([null, null]);
        setOrderNumberInput("");
        setTrackingNumberInput("");
        setTrackingStatus(null);
        setTrackingType(null);
        setSellerFilter(null);
        setDeliveryPartnerFilter(null);
        setFilters((prev) => ({
            ...prev,
            page: 1,
            start_date: null,
            end_date: null,
            orderNumber: "",
            trackingNumber: "",
            tracking_status: null,
            tracking_type: null,
            seller_id: null,
            delivery_partner_id: null,
        }));
    };

    const onPageSizeChange = (newPageSize) => {
        const size = newPageSize === "All" ? 10 : (Number(newPageSize) || 10);
        setFilters((prev) => ({
            ...prev,
            page: 1,
            pageSize: size,
        }));
    };

    const onPageChange = (page) => {
        setFilters((prev) => ({
            ...prev,
            page,
        }));
    };

    // Shared download utility
    const downloadFile = (url) => {
        if (!url) return;
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "");
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const handleDownloadLabel = (row) => {
        if (!row?.id) return;
        dispatch(downloadTrackingLabel({ id: row.id })).then((res) => {
            if (res.payload) downloadFile(res.payload);
        });
    };

    const handleDownloadLabelQRCode = (row) => {
        if (!row?.id) return;
        dispatch(downloadTrackingLabelQRCode({ id: row.id })).then((res) => {
            if (res.payload) downloadFile(res.payload);
        });
    };

    const tableColumns = [
        {
            name: "Order",
            selector: (row) => {
                if (!row.orderName) return "-";
                if (row.orderId) {
                    return (
                        <Link
                            className="text-primary"
                            style={{ cursor: "pointer" }}
                            target="_blank"
                            rel="noopener noreferrer"
                            to={`${process.env.PUBLIC_URL || ""}/order/${row.orderId}/view`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {row.orderName}
                        </Link>
                    );
                }
                return row.orderName;
            },
            left: true,
        },
        {
            name: "Tracking Date",
            selector: (row) => row.trackingDate ? moment(row.trackingDate).format("MMM DD, YYYY HH:mm") : "-",
            left: true,
        },
        {
            name: "Type",
            selector: (row) => row.type || "-",
            left: true,
        },
        {
            name: "Seller",
            selector: (row) => {
                if (row.sellerLists && row.sellerLists.length > 0) {
                    return row.sellerLists.join(", ");
                }
                return row.seller || "-";
            },
            left: true,
        },
        {
            name: "Delivery Partner",
            selector: (row) => row.deliveryPartner || "-",
            left: true,
        },
        {
            name: "Tracking",
            selector: (row) => {
                if (!row.trackingNumber && !row.trackingUrl) return "-";
                if (row.trackingUrl) {
                    return (
                        <a href={row.trackingUrl} target="_blank" rel="noopener noreferrer">
                            {row.trackingNumber || "Open"}
                        </a>
                    );
                }
                return row.trackingNumber || "-";
            },
            left: true,
        },
        {
            name: "Status",
            selector: (row) => {
                const status = row.trackingStatus || "Pending";
                const badgeClass = status === "Delivered"
                    ? "success"
                    : status === "In Transit"
                        ? "warning"
                        : "secondary";
                return <span className={`badge badge-${badgeClass}`}>{status}</span>;
            },
            center: true,
        },
        {
            name: "Items",
            selector: (row) => (row.items ? row.items.length : 0),
            center: true,
        },
        {
            name: "Actions",
            selector: (row) => {
                const canLabel = row.actions?.is_available_label;
                const canQr = row.actions?.is_available_label_qrcode;
                const isOrderType = row.type === "Order";
                return (
                    <div className="text-right" onClick={(e) => e.stopPropagation()}>
                        <Button
                            size="xs"
                            color="info"
                            className="mr-1"
                            onClick={(e) => {
                                e.stopPropagation();
                                history.push({
                                    pathname: `/order/trackings/${row.id}/view`,
                                    state: { trackingData: row }
                                });
                            }}
                            title="View Details"
                        >
                            <i className="fa fa-eye" />
                        </Button>
                        <Button
                            size="xs"
                            color="primary"
                            className="mr-1"
                            disabled={!canLabel || isOrderType}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadLabel(row);
                            }}
                            title="Download Label"
                        >
                            <i className="fa fa-download mr-1" />
                            Label
                        </Button>
                        <Button
                            size="xs"
                            color="secondary"
                            disabled={!canQr || isOrderType}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadLabelQRCode(row);
                            }}
                            title="Download QR Code"
                        >
                            <i className="fa fa-qrcode mr-1" />
                            QR
                        </Button>
                    </div>
                );
            },
            center: true,
            grow: 0.4,
        },
    ];

    const selectedTrackingStatus = trackingStatusOptions.find((opt) => opt.id === trackingStatus?.id) || trackingStatus;
    const selectedTrackingType = trackingTypeOptions.find((opt) => opt.id === trackingType?.id) || trackingType;

    return (
        <Fragment>
            <Breadcrumb title="Trackings" data={dataMenu} />
            <Container fluid={true} className="finances-summary-page">
                <Card className="px-3 mb-3">
                    <CardBody>
                        <Row>
                            <Col md="12">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0">Filters</h6>
                                    <Button
                                        color="primary"
                                        size="sm"
                                        className="btn-primary"
                                        style={{ backgroundColor: '#7366ff', borderColor: '#7366ff', color: '#fff' }}
                                        onClick={clearFilters}
                                    >
                                        Clear Filters
                                    </Button>
                                </div>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="3">
                                <FormGroup>
                                    <Label>Date Range</Label>
                                    <DateRangePickerDefault
                                        dateRange={dateRange}
                                        dateRangeChange={(date) => {
                                            if (date === null) {
                                                setDateRange([null, null]);
                                            } else {
                                                setDateRange(date);
                                            }
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="3">
                                <FormGroup>
                                    <Label for="filterOrderNumber">Order Number</Label>
                                    <Input
                                        type="text"
                                        id="filterOrderNumber"
                                        placeholder="Search by order number"
                                        value={orderNumberInput}
                                        onChange={(e) => {
                                            setOrderNumberInput(e.target.value);
                                            handleTextInputChange("orderNumber", e.target.value);
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="3">
                                <FormGroup>
                                    <Label for="filterTrackingNumber">Tracking Number</Label>
                                    <Input
                                        type="text"
                                        id="filterTrackingNumber"
                                        placeholder="Search by tracking number"
                                        value={trackingNumberInput}
                                        onChange={(e) => {
                                            setTrackingNumberInput(e.target.value);
                                            handleTextInputChange("trackingNumber", e.target.value);
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="3">
                                <FormGroup>
                                    <Label>Tracking Status</Label>
                                    <Select
                                        className="select2-filter"
                                        placeholder="Filter by status"
                                        getOptionLabel={(option) => option.name}
                                        getOptionValue={(option) => option.id}
                                        value={selectedTrackingStatus}
                                        onChange={(e) => {
                                            setTrackingStatus(e);
                                            handleFilterChange("tracking_status", e ? e.id : null);
                                        }}
                                        components={{
                                            IndicatorSeparator: () => null,
                                            DropdownIndicator: (props) => {
                                                return trackingStatus ? null : <components.DropdownIndicator {...props} />;
                                            },
                                        }}
                                        isClearable={true}
                                        options={trackingStatusOptions}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="4">
                                <FormGroup>
                                    <Label>Tracking Type</Label>
                                    <Select
                                        className="select2-filter"
                                        placeholder="Filter by type"
                                        styles={{ control: (base) => ({ ...base, width: "100%", minWidth: 0 }) }}
                                        getOptionLabel={(option) => option.name}
                                        getOptionValue={(option) => option.id}
                                        value={selectedTrackingType}
                                        onChange={(e) => {
                                            setTrackingType(e);
                                            handleFilterChange("tracking_type", e ? e.id : null);
                                        }}
                                        components={{
                                            IndicatorSeparator: () => null,
                                            DropdownIndicator: (props) => {
                                                return trackingType ? null : <components.DropdownIndicator {...props} />;
                                            },
                                        }}
                                        isClearable={true}
                                        options={trackingTypeOptions}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label>Seller</Label>
                                    <AsyncSelect
                                        className="select2-filter"
                                        placeholder="Filter by seller"
                                        styles={{ control: (base) => ({ ...base, width: "100%", minWidth: 0 }) }}
                                        cacheOptions
                                        defaultOptions
                                        loadOptions={sellerLoadOptions}
                                        getOptionLabel={(option) => option.label ?? option.name}
                                        getOptionValue={(option) => option.value ?? option.id}
                                        value={sellerFilter}
                                        onChange={(e) => {
                                            setSellerFilter(e);
                                            handleFilterChange("seller_id", e ? (e.value ?? e.id) : null);
                                        }}
                                        components={{ IndicatorSeparator: () => null }}
                                        isClearable={true}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label>Delivery Partner</Label>
                                    <Select
                                        className="select2-filter"
                                        placeholder="Filter by delivery partner"
                                        styles={{ control: (base) => ({ ...base, width: "100%", minWidth: 0 }) }}
                                        getOptionLabel={(option) => option.name}
                                        getOptionValue={(option) => option.id}
                                        value={deliveryPartnerFilter}
                                        onChange={(e) => {
                                            setDeliveryPartnerFilter(e);
                                            handleFilterChange("delivery_partner_id", e ? e.id : null);
                                        }}
                                        components={{
                                            IndicatorSeparator: () => null,
                                            DropdownIndicator: (props) => {
                                                return deliveryPartnerFilter ? null : <components.DropdownIndicator {...props} />;
                                            },
                                        }}
                                        isClearable={true}
                                        options={orderState.settings?.deliveryPartners ?? []}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                <Card className="px-3">
                    <CardBody>
                        <BootstrapTable
                            headerColumns={tableColumns}
                            gridData={trackingState.trackingsList}
                            noAction
                            progressPending={loading}
                            paginationServer
                            paginationTotalRows={trackingState.trackingsTableRow}
                            paginationRowsPerPageOptions={[10, 25, 50, 100]}
                            paginationAllowAll={false}
                            currentPage={filters.page}
                            perPage={filters.pageSize}
                            onChangePage={onPageChange}
                            onChangeRowsPerPage={onPageSizeChange}
                            size="md"
                            onRowClick={(row) => {
                                if (row?.id) {
                                    history.push({
                                        pathname: `/order/trackings/${row.id}/view`,
                                        state: { trackingData: row }
                                    });
                                }
                            }}
                            pointerOnHover
                        />
                    </CardBody>
                </Card>
            </Container>
        </Fragment>
    );
};

export default withReducer([{ order: orderReducer }])(OrderTrackings);